import React, { useMemo, useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import AppSelect from 'src/components/common/AppSelect';

const mockRequirements = [
  {
    id: 'REQ-221',
    title: 'Frontend Engineer',
    closingDate: '2026-03-12',
    locations: [
      { name: 'Bengaluru', headcount: 4 },
      { name: 'Chennai', headcount: 2 },
    ],
  },
  {
    id: 'REQ-222',
    title: 'Data Analyst',
    closingDate: '2026-03-20',
    locations: [
      { name: 'Hyderabad', headcount: 3 },
      { name: 'Pune', headcount: 2 },
      { name: 'Remote', headcount: 1 },
    ],
  },
  {
    id: 'REQ-223',
    title: 'Cloud Engineer',
    closingDate: '2026-04-05',
    locations: [
      { name: 'Mumbai', headcount: 2 },
      { name: 'Noida', headcount: 3 },
    ],
  },
];

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
};

const getTotalHeadcount = (locations) =>
  locations.reduce((total, location) => total + location.headcount, 0);

const CreateJobPosting = () => {
  const navigate = useNavigate();
  const [selectedRequirementId, setSelectedRequirementId] = useState('');

  const selectedRequirement = useMemo(
    () =>
      mockRequirements.find(
        (requirement) => requirement.id === selectedRequirementId
      ),
    [selectedRequirementId]
  );

  const handleCreate = () => {
    if (!selectedRequirement) return;
    navigate('/job-postings');
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Create Job Posting"
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={() => navigate('/job-postings')}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
            Back to list
          </Button>
        }
      />

      <div className="flex-1 p-6 bg-muted">
        <div className="bg-card rounded-lg shadow-sm p-6 space-y-6">
          <div>
            <div
              htmlFor="requirement-select"
              className="block text-sm font-semibold leading-6 text-foreground-strong"
            >
              Requirement
              <span className="text-red-500 ml-1">*</span>
            </div>
            <div className="mt-2">
              <AppSelect
                options={mockRequirements.map((r) => ({
                  value: r.id,
                  label: `${r.title} (${r.id})`,
                }))}
                value={selectedRequirementId}
                onValueChange={setSelectedRequirementId}
                id="requirement-select"
                placeholder="Select a requirement"
                className="w-full"
              />
            </div>
          </div>

          {selectedRequirement ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-md border bg-muted p-4">
                  <div className="text-xs text-muted-foreground">
                    Requirement ID
                  </div>
                  <div className="text-sm font-semibold text-foreground-strong">
                    {selectedRequirement.id}
                  </div>
                </div>
                <div className="rounded-md border bg-muted p-4">
                  <div className="text-xs text-muted-foreground">
                    Closing Date
                  </div>
                  <div className="text-sm font-semibold text-foreground-strong">
                    {formatDate(selectedRequirement.closingDate)}
                  </div>
                </div>
                <div className="rounded-md border bg-muted p-4 md:col-span-2">
                  <div className="text-xs text-muted-foreground">
                    Total Required Count
                  </div>
                  <div className="text-sm font-semibold text-foreground-strong">
                    {getTotalHeadcount(selectedRequirement.locations)}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-foreground-strong mb-2">
                  Locations and Headcounts
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {selectedRequirement.locations.map((location) => (
                    <div
                      key={`${selectedRequirement.id}-${location.name}`}
                      className="rounded-md border bg-muted p-4"
                    >
                      <div className="text-xs text-muted-foreground">
                        Location
                      </div>
                      <Input
                        value={location.name}
                        disabled
                        className="mt-1 bg-card"
                      />
                      <div className="text-xs text-muted-foreground mt-4">
                        Headcount
                      </div>
                      <Input
                        value={String(location.headcount)}
                        disabled
                        className="mt-1 bg-card"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleCreate}>Create Job Post</Button>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
              Select a requirement to prefill the locations and headcounts.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateJobPosting;
